//@ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { Button, Loading } from "../../atoms";
import { Table } from "../../molecules";
import { useGetBankList } from "../../../hooks";
import { GridColDef } from "@mui/x-data-grid";
import { useGetIksTerminalRequests, IksTerminalRequest } from "../../../hooks/iks/useGetIksTerminalRequests";
import { useInsertIksTerminalRequest } from "../../../hooks/iks/useInsertIksTerminalRequest"; // New import
import { useSetSnackBar } from "../../../store/Snackbar.state"; // New import

interface IksOperationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  merchantId: number | null;
  onOpenIksQuery: (referenceCode: string) => void;
}

export const IksOperationsModal = ({
  isOpen,
  onClose,
  merchantId,
  onOpenIksQuery,
}: IksOperationsModalProps) => {
  const terminalTypeOptions = [
    { label: "SanalPos", value: "S" },
    { label: "SoftPos", value: "M" },
  ];

  const [view, setView] = useState<"list" | "add">("list");
  const [selectedBank, setSelectedBank] = useState<{ label: string; value: string } | null>(null); // New state
  const [bankToAdd, setBankToAdd] = useState<{ label: string; value: string } | null>(null); // New state
  const [terminalTypeToAdd, setTerminalTypeToAdd] = useState<{ label: string; value: string }>(terminalTypeOptions[0]);
  const { data: rawBankList, isLoading: isBankListLoading } = useGetBankList({});

  const setSnackbar = useSetSnackBar(); // New instance

  const iksTerminalRequestsPayload = useMemo(() => {
    if (!merchantId) return null;

    const filters: any[] = [
      {
        field: "merchantId",
        values: [merchantId.toString()],
        valueType: 2,
        operationType: 6,
      },
    ];

    if (selectedBank) {
      filters.push({
        field: "bankCode",
        values: [selectedBank.value],
        valueType: 1,
        operationType: 6,
      });
    }

    return {
      page: 0,
      size: 10,
      orderByDesc: true,
      orderBy: "CreateDate",
      filters: filters,
    };
  }, [merchantId, selectedBank]);

  const { data: iksTerminalRequestsData, isLoading: isIksTerminalRequestsLoading, refetch } =
    useGetIksTerminalRequests(iksTerminalRequestsPayload, {
      enabled: isOpen && !!merchantId,
    });

  const { mutate: insertIks, isLoading: isInserting } = useInsertIksTerminalRequest(); // New mutation

  useEffect(() => {
    if (isOpen) {
      setView("list");
      refetch(); // Refetch the list
      setSelectedBank(null); // Reset filter on open
      setBankToAdd(null); // Reset add form on open
      setTerminalTypeToAdd(terminalTypeOptions[0]); // Reset type selection on open
    }
  }, [isOpen, refetch]);

  const bankList = useMemo(() => {
    return (
      rawBankList?.data?.map((bank: { name: string; code: string }) => ({
        label: bank.name,
        value: bank.code,
      })) || []
    );
  }, [rawBankList?.data]);

  const tableRows = useMemo(() => {
    if (!iksTerminalRequestsData?.data?.result) return [];

    return iksTerminalRequestsData.data.result.map(item => {
      const bank = bankList.find(b => b.value === item.bankCode);
      return {
        ...item,
        bankName: bank ? bank.label : item.bankCode, // Fallback to code if not found
      };
    });
  }, [iksTerminalRequestsData, bankList]);

  const columns: GridColDef[] = [
    { field: "bankName", headerName: "Banka Adı", flex: 1 },
    {
      field: "referenceCode",
      headerName: "Referans Kodu",
      flex: 1,
      renderCell: (params) => (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            onOpenIksQuery(params.value);
          }}
          style={{ textDecoration: "underline", color: "blue", cursor: "pointer" }}
        >
          {params.value}
        </a>
      ),
    },
    {
      field: "status", headerName: "Durum", flex: 1, renderCell: (params) => {
        switch (params.value) {
          case "ACTIVE":
            return "Aktif";
          case "Success":
            return "Başarılı";
          default:
            return params.value;
        }
      },
    },
    { field: "responseCode", headerName: "Yanıt Kodu", flex: 1 },
    { field: "responseCodeExplanation", headerName: "Yanıt Açıklaması", flex: 2 },
  ];

  const handleSave = () => {
    if (!bankToAdd || !merchantId) {
      setSnackbar({
        severity: "error",
        description: "Lütfen bir banka seçin.",
        isOpen: true,
      });
      return;
    }

    const payload = {
      merchantId: merchantId,
      bankCode: bankToAdd.value,
      type: terminalTypeToAdd.value,
    };

    insertIks(payload, {
      onSuccess: (data) => {
        if (data.isSuccess) {
          setSnackbar({
            severity: "success",
            description: "IKS terminal isteği başarıyla eklendi.",
            isOpen: true,
          });
          refetch(); // Refetch the list
          setView("list"); // Go back to the list view
          setBankToAdd(null); // Reset the selection
        } else {
          setSnackbar({
            severity: "error",
            description: data.message || "Bir hata oluştu.",
            isOpen: true,
          });
        }
      },
      onError: (error) => {
        setSnackbar({
          severity: "error",
          description: "İşlem sırasında bir hata oluştu.",
          isOpen: true,
        });
      },
    });
  };

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="md">
      {(isIksTerminalRequestsLoading || isInserting) && <Loading />} {/* Combined loading */}
      <DialogTitle>
        {view === "list"
          ? `IKS İşlemleri: Üye İşyeri ${merchantId}`
          : "Yeni IKS Ekle"}
      </DialogTitle>
      <DialogContent>
        {view === "list" ? (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Autocomplete
                options={bankList}
                loading={isBankListLoading}
                getOptionLabel={(option) => option.label}
                value={selectedBank} // Added value prop
                onChange={(event, newValue) => { // Added onChange handler
                  setSelectedBank(newValue);
                }}
                renderInput={(params) => <TextField {...params} label="Banka" />}
                sx={{ width: 200 }}
              />
              <Button text="Ekle" onClick={() => setView("add")} />
            </Box>
            <Table
              columns={columns}
              rows={tableRows}
              rowCount={iksTerminalRequestsData?.data?.totalItems || 0}
              paginationMode="server"
            />
          </Stack>
        ) : (
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Stack direction="row" spacing={2}>
              <Autocomplete
                options={bankList}
                loading={isBankListLoading}
                getOptionLabel={(option) => option.label}
                value={bankToAdd}
                onChange={(event, newValue) => {
                  setBankToAdd(newValue);
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Banka Seçiniz" />
                )}
                sx={{ flex: 1 }}
              />
              <Autocomplete
                options={terminalTypeOptions}
                getOptionLabel={(option) => option.label}
                value={terminalTypeToAdd}
                onChange={(event, newValue) => {
                  if (newValue) setTerminalTypeToAdd(newValue);
                }}
                disableClearable
                renderInput={(params) => (
                  <TextField {...params} label="Terminal Tipi" />
                )}
                sx={{ flex: 1 }}
              />
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {view === "list" ? (
          <Button onClick={onClose} text="Kapat" variant="outlined" />
        ) : (
          <>
            <Button
              onClick={() => {
                setView("list");
                setBankToAdd(null); // Reset selection on cancel
              }}
              text="İptal"
              variant="outlined"
            />
            <Button onClick={handleSave} text="Kaydet" />
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
